import { Icon } from '@examdojo/core/icon';

export type StatTestType =
  | 'one-sample-t'
  | 'two-sample-t'
  | 'paired-t'
  | 'chi-squared-independence'
  | 'chi-squared-gof'
  | 'sign-test';

export interface TestDataColumn {
  readonly header: string;
  readonly colType: 'number' | 'text';
  readonly values: ReadonlyArray<number | string | null>;
}

export interface TestLibraryDataset {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly icon: Icon;
  readonly testType: StatTestType;
  readonly columns: readonly TestDataColumn[];
}

export const STAT_TEST_LABELS: Readonly<Record<StatTestType, string>> = {
  'one-sample-t': 'One-sample t-test',
  'two-sample-t': 'Two-sample t-test',
  'paired-t': 'Paired t-test',
  'chi-squared-independence': '\u03C7\u00B2 test for independence',
  'chi-squared-gof': '\u03C7\u00B2 goodness of fit',
  'sign-test': 'Sign test',
};

export interface TestTypeGroup {
  readonly label: string;
  readonly testTypes: readonly StatTestType[];
}

export const TEST_TYPE_GROUPS: readonly TestTypeGroup[] = [
  { label: 't-Tests', testTypes: ['one-sample-t', 'two-sample-t', 'paired-t'] },
  { label: 'Chi-Squared Tests', testTypes: ['chi-squared-independence', 'chi-squared-gof'] },
  { label: 'Non-parametric Tests', testTypes: ['sign-test'] },
];

// ─────────────────────────────────────────────────────────────────────────────
// One-sample t-test datasets
// ─────────────────────────────────────────────────────────────────────────────

const IB_EXAM_SCORES: TestLibraryDataset = {
  id: 'one-sample-ib-scores',
  name: 'IB Exam Scores',
  description: 'Test if class mean differs from global average (\u03BC\u2080 = 4.5)',
  icon: 'graduation-cap',
  testType: 'one-sample-t',
  columns: [
    {
      header: 'Score',
      colType: 'number',
      values: [5, 6, 4, 7, 5, 6, 5, 4, 6, 7, 5, 6, 5, 6, 5],
    },
  ],
};

const BATTERY_LIFE: TestLibraryDataset = {
  id: 'one-sample-battery',
  name: 'Battery Life',
  description: 'Test if battery life meets claimed 500 hours',
  icon: 'battery-full',
  testType: 'one-sample-t',
  columns: [
    {
      header: 'Hours',
      colType: 'number',
      values: [498, 502, 497, 501, 499, 503, 496, 500, 502, 498, 501, 497],
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Two-sample t-test datasets
// ─────────────────────────────────────────────────────────────────────────────

const PLANT_GROWTH: TestLibraryDataset = {
  id: 'two-sample-plants',
  name: 'Plant Growth',
  description: 'Comparing plant heights (cm) with two fertilizers',
  icon: 'seedling',
  testType: 'two-sample-t',
  columns: [
    {
      header: 'Fertilizer A',
      colType: 'number',
      values: [14.2, 15.8, 13.5, 16.1, 14.9, 15.3, 14.7, 15.5, 13.9, 15.1],
    },
    {
      header: 'Fertilizer B',
      colType: 'number',
      values: [17.3, 18.1, 16.5, 19.2, 17.8, 18.5, 17.1, 18.8, 16.9, 17.6],
    },
  ],
};

const TEACHING_METHODS: TestLibraryDataset = {
  id: 'two-sample-teaching',
  name: 'Teaching Methods',
  description: 'Comparing scores between traditional and inquiry-based methods',
  icon: 'book-open',
  testType: 'two-sample-t',
  columns: [
    {
      header: 'Traditional',
      colType: 'number',
      values: [72, 68, 75, 71, 69, 73, 70, 74, 67, 71, 73, 69],
    },
    {
      header: 'Inquiry',
      colType: 'number',
      values: [78, 82, 75, 80, 77, 83, 79, 76, 81, 78],
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Paired t-test datasets
// ─────────────────────────────────────────────────────────────────────────────

const BLOOD_PRESSURE: TestLibraryDataset = {
  id: 'paired-blood-pressure',
  name: 'Blood Pressure',
  description: 'Systolic BP before and after 6-week exercise program',
  icon: 'heart-pulse',
  testType: 'paired-t',
  columns: [
    {
      header: 'Before',
      colType: 'number',
      values: [142, 138, 145, 150, 135, 148, 140, 152, 137, 143],
    },
    {
      header: 'After',
      colType: 'number',
      values: [136, 132, 140, 142, 130, 141, 135, 145, 133, 138],
    },
  ],
};

const MEMORY_TRAINING: TestLibraryDataset = {
  id: 'paired-memory',
  name: 'Memory Training',
  description: 'Words recalled before and after study technique training',
  icon: 'brain',
  testType: 'paired-t',
  columns: [
    {
      header: 'Before',
      colType: 'number',
      values: [12, 15, 11, 13, 14, 10, 12, 16, 11, 13],
    },
    {
      header: 'After',
      colType: 'number',
      values: [15, 18, 14, 16, 17, 13, 15, 19, 14, 16],
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Chi-squared test for independence datasets
// ─────────────────────────────────────────────────────────────────────────────

const SUBJECT_PREFERENCE: TestLibraryDataset = {
  id: 'chi-ind-subjects',
  name: 'Subject Preference',
  description: 'Association between gender and favorite subject',
  icon: 'user-group',
  testType: 'chi-squared-independence',
  columns: [
    { header: 'Gender', colType: 'text', values: ['Male', 'Female'] },
    { header: 'Math', colType: 'number', values: [35, 20] },
    { header: 'Science', colType: 'number', values: [28, 22] },
    { header: 'English', colType: 'number', values: [15, 30] },
    { header: 'Arts', colType: 'number', values: [12, 18] },
  ],
};

const EXERCISE_BY_AGE: TestLibraryDataset = {
  id: 'chi-ind-exercise',
  name: 'Exercise by Age',
  description: 'Association between age group and exercise frequency',
  icon: 'person-running',
  testType: 'chi-squared-independence',
  columns: [
    { header: 'Age Group', colType: 'text', values: ['18\u201325', '26\u201340', '41\u201360'] },
    { header: 'Never', colType: 'number', values: [10, 15, 25] },
    { header: 'Sometimes', colType: 'number', values: [25, 30, 28] },
    { header: 'Often', colType: 'number', values: [35, 25, 17] },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Chi-squared goodness of fit datasets
// ─────────────────────────────────────────────────────────────────────────────

const FAIR_DIE: TestLibraryDataset = {
  id: 'chi-gof-die',
  name: 'Fair Die',
  description: 'Testing if a die produces uniform results (120 rolls)',
  icon: 'dice',
  testType: 'chi-squared-gof',
  columns: [
    { header: 'Face', colType: 'text', values: ['1', '2', '3', '4', '5', '6'] },
    { header: 'Observed', colType: 'number', values: [18, 22, 15, 25, 12, 28] },
  ],
};

const MENDELS_PEAS: TestLibraryDataset = {
  id: 'chi-gof-mendel',
  name: "Mendel's Peas",
  description: 'Testing observed ratios against expected 9:3:3:1',
  icon: 'leaf',
  testType: 'chi-squared-gof',
  columns: [
    {
      header: 'Phenotype',
      colType: 'text',
      values: ['Round Yellow', 'Round Green', 'Wrinkled Yellow', 'Wrinkled Green'],
    },
    { header: 'Observed', colType: 'number', values: [315, 108, 101, 32] },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Sign test datasets
// ─────────────────────────────────────────────────────────────────────────────

const CHOCOLATE_TASTING: TestLibraryDataset = {
  id: 'sign-chocolate',
  name: 'Chocolate Tasting',
  description: 'Paired ratings comparing two chocolate brands (1\u201310)',
  icon: 'star',
  testType: 'sign-test',
  columns: [
    {
      header: 'Brand A',
      colType: 'number',
      values: [7, 8, 6, 9, 5, 7, 8, 6, 7, 8, 6, 7],
    },
    {
      header: 'Brand B',
      colType: 'number',
      values: [8, 7, 8, 9, 6, 8, 9, 7, 8, 9, 7, 8],
    },
  ],
};

export const TEST_LIBRARY_DATASETS: readonly TestLibraryDataset[] = [
  IB_EXAM_SCORES,
  BATTERY_LIFE,
  PLANT_GROWTH,
  TEACHING_METHODS,
  BLOOD_PRESSURE,
  MEMORY_TRAINING,
  SUBJECT_PREFERENCE,
  EXERCISE_BY_AGE,
  FAIR_DIE,
  MENDELS_PEAS,
  CHOCOLATE_TASTING,
];
